import * as THREE from 'three';
import EventEmitter from './EventEmitter.js';
import { Renderer } from '../Core/Renderer.js';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';

export default class Resources extends EventEmitter {
    static instance = null;

    static getInstance() {
        if (!Resources.instance) {
            Resources.instance = new Resources();
        }
        return Resources.instance;
    }

    constructor() {
        if (Resources.instance) {
            return Resources.instance;
        }

        super();
        Resources.instance = this;

        const isMobile = window.innerWidth < 768;

        const imagesPathsDesktop = {
            gtLogo: `${ window.theme_assets_url }/images/game-theory/animations/gtlogo4.ktx2`,
            umbrella_01: `${ window.theme_assets_url }/images/game-theory/animations/umbrella-01.ktx2`,
            umbrella_02: `${ window.theme_assets_url }/images/game-theory/animations/umbrella-03-1.ktx2`,
        }

        const imagesPathsMobile = {
            gtLogo: `${ window.theme_assets_url }/images/game-theory/animations/gtlogo-mobile.ktx2`,
            umbrella_01: `${ window.theme_assets_url }/images/game-theory/animations/umbrella-01-mobile.ktx2`,
            umbrella_02: `${ window.theme_assets_url }/images/game-theory/animations/umbrella-03-mobile.ktx2`,
        }

        this.items = {
            gtLogo: {
                name: 'gtLogo',
                type: 'textureKTX2',
                path: isMobile ? imagesPathsMobile.gtLogo : imagesPathsDesktop.gtLogo,
                priority: 1
            },
            umbrella_01: {
                name: 'umbrella_01',
                type: 'textureKTX2',
                path: isMobile ? imagesPathsMobile.umbrella_01 : imagesPathsDesktop.umbrella_01,
                priority: 2
            },
            /*umbrella_02: {
                name: 'umbrella_02',
                type: 'textureKTX2',
                path: isMobile ? imagesPathsMobile.umbrella_02 : imagesPathsDesktop.umbrella_02,
                priority: 2
            },*/
            subtract: {
                name: 'subtract',
                type: 'texture',
                path: `${ window.theme_assets_url }/images/game-theory/animations/subtract.png`,
                group: 'subtract',
                priority: 3
            },
            subtractBlur: {
                name: 'subtractBlur',
                type: 'texture',
                path: `${ window.theme_assets_url }/images/game-theory/animations/subtract-glow.png`,
                group: 'subtract',
                priority: 3
            },
            heroVideo: {
                name: 'heroVideo',
                type: 'video',
                paths: {
                    landscape: {
                        webm: `${ window.theme_assets_url }/images/game-theory/animations/hero.webm`,
                        mp4: `${ window.theme_assets_url }/images/game-theory/animations/hero.mp4`
                    },
                    portrait: {
                        webm: `${ window.theme_assets_url }/images/game-theory/animations/hero-portrait.webm`,
                        mp4: `${ window.theme_assets_url }/images/game-theory/animations/hero-portrait.mp4`
                    }
                },
                priority: 4
            },
        };

        this.renderer = Renderer.getInstance().instance;

        this.toLoad = Object.keys(this.items).length;
        this.loaded = 0;
        this.errors = 0;
        this.loaders = {};
        
        this.groups = {};
        this.initializeGroups();
        
        this.setLoaders();
        this.startLoading();
    }

    initializeGroups() {
        for (const key in this.items) {
            const item = this.items[key];
            if (item.group) {
                if (!this.groups[item.group]) {
                    this.groups[item.group] = {
                        total: 0,
                        loaded: 0
                    };
                }
                this.groups[item.group].total++;
            }
        }
    }

    checkGroupComplete(groupName) {
        const group = this.groups[groupName];
        if (group && group.loaded === group.total) {
            this.trigger('group', [groupName]);
        }
    }

    getGroupResources(groupName) {
        const resources = {};
        for (const key in this.items) {
            const item = this.items[key];
            if (item.group === groupName && this.items[key] !== null) {
                resources[item.name] = this.items[key];
            }
        }
        return resources;
    }

    setLoaders() {
        this.loaders.textureLoader = new THREE.TextureLoader();
        this.loaders.ktx2Loader = new KTX2Loader();
        this.loaders.ktx2Loader.setTranscoderPath(`${ window.theme_assets_url }/js/vendors/basis/`);
        this.loaders.ktx2Loader.detectSupport(this.renderer);
    }

    startLoading() {
        // Sort items by priority
        const sortedItems = Object.entries(this.items)
            .sort(([, a], [, b]) => (a.priority || 0) - (b.priority || 0));

        for (const [, item] of sortedItems) {
            if (item.type === 'texture') {
                this.loaders.textureLoader.load(
                    item.path, 
                    (file) => this.onLoad(item, file),
                    undefined,
                    (error) => this.onError(item, error)
                )
            } else if (item.type === 'textureKTX2') {
                this.loaders.ktx2Loader.load(
                    item.path, 
                    (file) => this.onLoad(item, file)
                );
            } else if (item.type === 'video') {
                // Determine initial orientation
                const isPortrait = window.innerHeight > window.innerWidth;
                const orientationPath = isPortrait ? item.paths.portrait : item.paths.landscape;
                
                let videoElement = document.createElement('video');
                videoElement.setAttribute('crossorigin', 'anonymous');
                videoElement.muted = true;
                videoElement.loop = true;
                videoElement.setAttribute('playsinline', '');
                videoElement.setAttribute('webkit-playsinline', '');
                
                // Try WebM first, with MP4 as fallback
                if (this.canPlayWebm()) {
                    videoElement.src = orientationPath.webm;
                } else {
                    videoElement.src = orientationPath.mp4;
                }
                
                videoElement.load();
                videoElement.pause();
                
                // Store current orientation
                videoElement.dataset.orientation = isPortrait ? 'portrait' : 'landscape';
                
                // handle error
                videoElement.onerror = () => {
                    // If WebM fails, try MP4
                    if (videoElement.src === orientationPath.webm) {
                        videoElement.src = orientationPath.mp4;
                        videoElement.load();
                    } else {
                        this.onVideoError(item);
                    }
                }

                const obj = {
                    videoTexture: new THREE.VideoTexture(videoElement),
                    videoElement: videoElement,
                    videoWidth: 0,
                    videoHeight: 0,
                    isVideoTexture: true,
                    paths: item.paths // Store paths for orientation changes
                };

                videoElement.addEventListener('canplaythrough', () => {
                    obj.videoWidth = videoElement.videoWidth;
                    obj.videoHeight = videoElement.videoHeight;
                    this.onVideoLoaded(item, obj);
                }, { once: true });
            }
        }
    }

    onVideoLoaded(item, obj) {
        this.items[item.name] = obj;
        this.handleLoaded(item);
    }

    onError(item, error) {
        this.errors++;
    
        const emptyTexture = new THREE.Texture();
        
        // Store the empty texture
        this.items[item.name] = emptyTexture;
        
        this.handleLoaded(item);
    }

    onVideoError(item) {
        this.items[item.name] = null;
        this.handleLoaded(item);
    }

    handleLoaded(item) {
        // Trigger resource event
        this.trigger('resource', [item.name]);
        
        // Update group loading status
        if (item.group) {
            this.groups[item.group].loaded++;
            this.checkGroupComplete(item.group);
        }
        
        this.loaded++;
        this.triggerProgress();
        if (this.loaded === this.toLoad) {
            this.trigger('ready');
        }
    }

    onLoad(item, file) {
        if (item.type === 'texture') {
            file.colorSpace = item.colorSpace ? item.colorSpace : THREE.SRGBColorSpace;
            file.minFilter = item.minFilter ? item.minFilter : THREE.LinearFilter;
            file.magFilter = item.magFilter ? item.magFilter : THREE.LinearFilter;
            file.generateMipmaps = item.generateMipmaps ? item.generateMipmaps : false;
            file.wrapS = item.wrapS ? item.wrapS : THREE.ClampToEdgeWrapping;
            file.wrapT = item.wrapT ? item.wrapT : THREE.ClampToEdgeWrapping;

            if (this.renderer) {
                file.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
            }

            file.needsUpdate = true;
        } else if (item.type === 'textureKTX2') {
            file.colorSpace = item.colorSpace ? item.colorSpace : THREE.SRGBColorSpace;
            file.minFilter = item.minFilter ? item.minFilter : THREE.LinearFilter;
            file.magFilter = item.magFilter ? item.magFilter : THREE.LinearFilter;
            file.generateMipmaps = false;
            file.wrapS = item.wrapS ? item.wrapS : THREE.ClampToEdgeWrapping;
            file.wrapT = item.wrapT ? item.wrapT : THREE.ClampToEdgeWrapping;

            file.needsUpdate = true;
        }
        this.items[item.name] = file;
        this.handleLoaded(item);
    }
    
    triggerProgress() {
        const progress = this.loaded / this.toLoad;
        this.trigger('progress', {
            progress: progress,
            loaded: this.loaded,
            total: this.toLoad,
            errors: this.errors
        });
    }

    // Check if browser supports WebM format
    canPlayWebm() {
        const video = document.createElement('video');
        return video.canPlayType('video/webm').length > 0;
    }

    // Add method to reload video for orientation change
    reloadVideoForOrientation(videoName, isPortrait) {
        const item = this.items[videoName];
        if (!item || item.type !== 'video') return;

        const currentVideo = this.items[videoName];
        if (!currentVideo) return;

        // Clean up old video
        if (currentVideo.videoElement) {
            currentVideo.videoElement.pause();
            currentVideo.videoElement.src = '';
            currentVideo.videoElement.load();
        }
        if (currentVideo.videoTexture) {
            currentVideo.videoTexture.dispose();
        }

        // Create new video element
        const orientationPath = isPortrait ? item.paths.portrait : item.paths.landscape;
        let videoElement = document.createElement('video');
        videoElement.setAttribute('crossorigin', 'anonymous');
        videoElement.muted = true;
        videoElement.loop = true;
        videoElement.setAttribute('playsinline', '');
        videoElement.setAttribute('webkit-playsinline', '');
        
        // Try WebM first, with MP4 as fallback
        if (this.canPlayWebm()) {
            videoElement.src = orientationPath.webm;
        } else {
            videoElement.src = orientationPath.mp4;
        }
        
        videoElement.load();
        videoElement.dataset.orientation = isPortrait ? 'portrait' : 'landscape';

        const obj = {
            videoTexture: new THREE.VideoTexture(videoElement),
            videoElement: videoElement,
            videoWidth: 0,
            videoHeight: 0,
            isVideoTexture: true,
            paths: item.paths
        };

        videoElement.addEventListener('canplaythrough', () => {
            obj.videoWidth = videoElement.videoWidth;
            obj.videoHeight = videoElement.videoHeight;
            this.items[videoName] = obj;
            this.trigger('resource', [videoName]);
        }, { once: true });
        
        // Handle error - try MP4 if WebM fails
        videoElement.onerror = () => {
            if (videoElement.src === orientationPath.webm) {
                videoElement.src = orientationPath.mp4;
                videoElement.load();
            }
        };
    }
}
